import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import cn from "classnames";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { defineMessages, useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  EntityTypesSupportingFieldCustomization,
  EntityTypeSupportingGroups,
  Group,
} from "@mapmycustomers/shared/types/entity";
import type Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import type Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import FormLayout, {
  ChildLayout,
  LayoutSchemaField,
} from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert } from "@mapmycustomers/ui";

import { getFunnelStages } from "@app/store/deal";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { getEntityTypeDisplayName } from "@app/util/ui";

import {
  getActiveEntityType,
  getEditedChildLayoutId,
  getEditedLayout,
  getEditedLayoutSortedSchema,
} from "../../store";
import { updateChildLayout, updateLayout } from "../../store/actions";
import ActivityNotification from "../ActivityNotification";
import DealNotification from "../DealNotification";
import EditDefaultValueModal from "../EditDefaultValueModal";
import TemporaryFieldsCheckbox from "../TemporaryFieldsCheckbox";

import styles from "./Canvas.module.scss";
import SchemaField from "./SchemaField";
import useDefaultValueChange from "./util/useDefaultValueChange";
import useLayoutFields from "./util/useLayoutFields";

const messages = defineMessages({
  addressAlert: {
    id: "settings.formLayouts.canvas.addressAlert",
    defaultMessage:
      "You’ve removed the Address field. Any records created using this layout will not be mapped.",
    description: "Alert when user removes address field",
  },
  title: {
    id: "settings.formLayouts.canvas.formTitle",
    defaultMessage: "Create {type}",
    description: "Title of Canvas form title",
  },
  titleActivity: {
    id: "settings.formLayouts.canvas.formTitle.activities",
    defaultMessage: "Create or Edit {type}",
    description: "Title of Canvas form title for activities",
  },
});

interface Props {
  activeChildLayout?: ChildLayout["id"];
  dragOverIndex: null | number;
  entityType: EntityTypesSupportingFieldCustomization;
  funnelStages: Record<Funnel["id"], Stage[]>;
  groups: Group[];
  layout?: FormLayout;
  onUpdateChildLayout: typeof updateChildLayout;
  onUpdateLayout: typeof updateLayout;
  schema: Array<LayoutSchemaField>;
}

const Canvas: React.FC<Props> = ({
  activeChildLayout,
  dragOverIndex,
  entityType,
  funnelStages,
  groups,
  layout,
  onUpdateChildLayout,
  onUpdateLayout,
  schema,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const isActivityType = entityType === EntityType.ACTIVITY;

  const [displayAddressAlert, showAddressAlert, hideAddressAlert] = useBoolean(false);

  const childLayout = useMemo(() => {
    if (activeChildLayout && layout?.childLayouts?.length) {
      return layout.childLayouts.find((child) => child.id === activeChildLayout);
    }

    return undefined;
  }, [activeChildLayout, layout]);

  const [schemaField, setSchemaField] = useState<LayoutSchemaField | undefined>();

  const stageDefaultValue = Form.useWatch(DealFieldName.STAGE, form);
  const funnelDefaultValue = Form.useWatch(DealFieldName.FUNNEL, form);

  const defaultFunnelStage = useMemo<Partial<FunnelStageValue>>(() => {
    if (stageDefaultValue) {
      const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
        funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === stageDefaultValue)
      );
      if (funnelIdString) {
        return {
          funnel: { id: parseInt(funnelIdString) },
          stage: { id: stageDefaultValue as Stage["id"] },
        };
      }
    }
    if (funnelDefaultValue) {
      return { funnel: { id: funnelDefaultValue as Funnel["id"] } };
    }
    return {};
  }, [funnelDefaultValue, funnelStages, stageDefaultValue]);

  const handleCloseDefaultValueModal = useCallback(() => {
    setSchemaField(undefined);
  }, [setSchemaField]);

  const activeSchema = useMemo(() => {
    if (childLayout) {
      return (childLayout.schema ?? []).sort((a: LayoutSchemaField, b: LayoutSchemaField) =>
        a.displayOrder < b.displayOrder ? -1 : a.displayOrder > b.displayOrder ? 1 : 0
      );
    }
    return schema;
  }, [childLayout, schema]);

  const handleDefaultValueChange = useDefaultValueChange(
    form,
    activeSchema,
    entityType,
    schemaField,
    onUpdateChildLayout,
    onUpdateLayout,
    activeChildLayout
  );

  useLayoutFields(form, activeSchema, entityType, childLayout, funnelStages);

  if (!layout || !entityType) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {intl.formatMessage(isActivityType ? messages.titleActivity : messages.title, {
          type: getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: false }),
        })}
      </div>
      {schemaField && (
        <EditDefaultValueModal
          defaultFunnelStage={defaultFunnelStage}
          entityType={entityType}
          groups={groups}
          onDefaultValueChange={handleDefaultValueChange}
          onHide={handleCloseDefaultValueModal}
          schemaField={schemaField}
        />
      )}

      <ActivityNotification />
      <DealNotification />

      {displayAddressAlert && (
        <div className={styles.alerts}>
          <Alert
            closable
            message={intl.formatMessage(messages.addressAlert)}
            onClose={hideAddressAlert}
            type={"warning"}
          />
        </div>
      )}

      <TemporaryFieldsCheckbox />

      <Form form={form} layout="vertical">
        <Droppable direction="vertical" droppableId="canvas">
          {(droppableProvided, droppableSnapshot) => {
            const isOver = droppableSnapshot.isDraggingOver;
            const isOverLast =
              isOver && dragOverIndex !== null && dragOverIndex >= activeSchema.length;
            let maxDisplayOrder = Number.NEGATIVE_INFINITY;

            return (
              <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                <div className={styles.list}>
                  {activeSchema.map((row: LayoutSchemaField, index) => {
                    maxDisplayOrder = Math.max(maxDisplayOrder, row.displayOrder);

                    return (
                      <SchemaField
                        activeSchema={activeSchema}
                        childLayout={childLayout}
                        defaultFunnelStage={defaultFunnelStage}
                        dragOverIndex={dragOverIndex}
                        form={form}
                        index={index}
                        isOver={isOver}
                        key={index}
                        onChangeSchemaField={setSchemaField}
                        row={row}
                        showAddressAlert={showAddressAlert}
                      />
                    );
                  })}
                </div>

                <Draggable draggableId={String(maxDisplayOrder + 1)} index={activeSchema.length}>
                  {(provided, snapshot) => (
                    <div
                      {...provided.draggableProps}
                      className={cn(styles.dropPlaceholder, {
                        [styles.active]: isOverLast,
                      })}
                      ref={provided.innerRef}
                      style={{
                        ...provided.draggableProps.style,
                        transform: snapshot.isDragging
                          ? provided.draggableProps.style?.transform
                          : "translate(0px, 0px)",
                        transition: "all 0.001s ease-out",
                      }}
                    >
                      <div {...provided.dragHandleProps}></div>
                    </div>
                  )}
                </Draggable>
                {droppableProvided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </Form>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const entityType = getActiveEntityType(state);
  return {
    activeChildLayout: getEditedChildLayoutId(state),
    entityType,
    funnelStages: getFunnelStages(state),
    groups: getGroupsForEntity(state)(entityType as EntityTypeSupportingGroups),
    layout: getEditedLayout(state),
    schema: getEditedLayoutSortedSchema(state),
  };
};

const mapDispatchToProps = {
  onUpdateChildLayout: updateChildLayout,
  onUpdateLayout: updateLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(Canvas);
